/**
 * Form Validation Hook
 * Single responsibility: Handle form validation logic and API calls
 */

import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../services/serviceTypes';
import { useLazyValidateApplicationNameQuery } from '../data-store/copilotApi';
import {
  type FormFieldData,
  handleApplicationNameError,
  handleUserGroupError,
  validateRequiredFields,
} from '../services/formValidationService';
import type { ParsedField } from '../utils/templateParsers';

const useFormValidation = () => {
  const [localFields, setLocalFields] = useState<ParsedField[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [applicationNamePrompt, setApplicationNamePrompt] =
    useState<boolean>(false);

  // Get endpointKey from Redux state
  const endpointKey = useSelector(
    (state: RootState) => state.copilot.ui.endpointKey
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const [validateApplicationName] = useLazyValidateApplicationNameQuery();

  const validateForm = useCallback(
    async (
      fields: ParsedField[],
      formData: Record<string, FormFieldData>,
      skipApplicationValidation = false
    ): Promise<boolean> => {
      const clientValidation = validateRequiredFields(fields, formData);

      if (!clientValidation.isValid) {
        // Check if UserGroup validation failed
        const hasUserGroupError = clientValidation.updatedFields.some(
          (field) =>
            field.attributeName === 'endpoints.ownerkey' &&
            (field as ParsedField & { hasError?: boolean }).hasError
        );

        if (hasUserGroupError) {
          const updatedFields = handleUserGroupError(
            clientValidation.updatedFields
          );

          setLocalFields(updatedFields);
        } else {
          setLocalFields(clientValidation.updatedFields);
        }

        return false;
      }

      setLocalFields(clientValidation.updatedFields);

      // Step 2: Server-side application name validation
      const applicationName = formData['securitysystems.systemname'].value;

      if (!applicationName || applicationName.trim() === '') {
        // If application name is empty, client validation should have caught it
        // Return the client validation result
        return clientValidation.isValid;
      }

      // Skip API validation if endpointKey exists (edit mode) or skipApplicationValidation is true
      if (endpointKey || skipApplicationValidation) {
        // Show success prompt (matches Copilot1)
        setApplicationNamePrompt(true);

        return true;
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        const result = await validateApplicationName(applicationName).unwrap();

        // API returns 'valid' string - check case-insensitively
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const isValid =
          String(result).toLowerCase() === 'valid' ||
          (result as unknown as { isValid?: boolean }).isValid === true;

        if (isValid) {
          // Show success prompt (matches Copilot1)
          setApplicationNamePrompt(true);

          return true;
        }

        // Handle duplicate application name
        const updatedFields = handleApplicationNameError(
          clientValidation.updatedFields
        );

        setLocalFields(updatedFields);

        return false;
      } catch (err) {
        // Handle API error (matches Copilot1)
        setError(true);

        return false;
      }
    },
    [validateApplicationName, endpointKey]
  );

  const resetValidationState = useCallback(() => {
    setError(false);
    setApplicationNamePrompt(false);
    setLocalFields([]);
  }, []);

  return {
    localFields,
    error,
    applicationNamePrompt,
    validateForm,
    resetValidationState,
    setApplicationNamePrompt,
  };
};

export default useFormValidation;
